import { ClientAwardGroupsEnum } from '~/meta/awards';
import { PartialVendorSearch } from '~/pages/couples/explore/components/SearchResults/types/types';
import {
  OptionFacetType,
  MetroTypes,
  CatererTypes,
  NumberOrMax,
  VendorSearchRangeType,
} from '~/types/types';

import { extractMinMaxFromSlug, MAX_RANGE_INDICATOR } from './helpers';

/**
 * The search filter controls use special (aka fake) facets to
 * represent price and capacity.  When this control gets a callback with
 * a special, aka fake, facet in it, we parse it out and extract the
 * overall min and max values and include those in the URL instead of
 * a bunch of different price/capacity facets.
 *
 * @param {*} specialFacets
 * @see couples/search/meta/facets
 */

export const extractMinMaxFromSpecialFacets: (
  specialFacets: OptionFacetType[]
) => VendorSearchRangeType | null = (specialFacets) => {
  let result: VendorSearchRangeType | null = null;
  specialFacets.forEach((priceFacet) => {
    const { min, max } = extractMinMaxFromSlug(priceFacet.slug);

    if (result === null) {
      result = {
        min,
        max,
      };
    } else {
      result = {
        min: Math.min(min, result.min),
        max: safeMax(max, result.max),
      };
    }
  });
  return result;
};

const safeMax = (candidate: NumberOrMax, max: NumberOrMax): NumberOrMax => {
  if (candidate === MAX_RANGE_INDICATOR || max === MAX_RANGE_INDICATOR) {
    return MAX_RANGE_INDICATOR;
  }
  return Math.max(candidate, max);
};

export function createVendorSearchFromFilters(facets: OptionFacetType[]): PartialVendorSearch {
  const newAwardGroups = [
    facets.filter((facet) => facet.slug.startsWith('award-groups')).map((type) => type.key),
  ] as ClientAwardGroupsEnum[][];

  const priceFacets = facets.filter((facet) => facet.slug.startsWith('price'));
  const newPrice = extractMinMaxFromSpecialFacets(priceFacets);
  const capacityFacets = facets.filter((facet) => facet.slug.startsWith('capacity'));
  const newCapacity = extractMinMaxFromSpecialFacets(capacityFacets);

  const newMetroTypes = facets
    .filter((facet) => facet.slug.startsWith('metro-types'))
    .map((type) => type.key) as MetroTypes[];

  const newCatererTypes = facets
    .filter((facet) => facet.slug.startsWith('caterer-type'))
    .map((type) => type.key) as CatererTypes[];

  const realFacets = facets.filter((facet) => {
    return !(
      facet.slug.startsWith('award-groups') ||
      facet.slug.startsWith('price') ||
      facet.slug.startsWith('capacity') ||
      facet.slug.startsWith('metro-types') ||
      facet.slug.startsWith('caterer-type')
    );
  });

  return {
    awardGroups: newAwardGroups,
    selectedFacets: realFacets,
    price: newPrice,
    capacity: newCapacity,
    metroTypes: newMetroTypes,
    catererTypes: newCatererTypes,
  };
}
